import { type ReactNode, useEffect, useState } from "react";

export function useResource<T>(
  loader: () => Promise<T>,
  deps: unknown[],
): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    loader()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}

export function Loading() {
  return (
    <p className="py-10 text-center font-mono text-[12px] font-bold tracking-[0.3em] text-ink-3">
      LOADING…
    </p>
  );
}

export function ErrorBox({ message }: { message: string }) {
  return (
    <p className="border-2 border-shu bg-shu-pale px-4 py-4 text-[13px] font-bold text-shu-deep">
      <span className="mr-2 bg-shu px-1.5 py-px text-[11px] text-white">エラー</span>
      {message}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="border border-dashed border-ink-3 px-4 py-8 text-center text-[13px] text-ink-2">{children}</p>
  );
}

/** Bottom sheet over the current screen; closing it never leaves the page. */
export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-30 flex flex-col justify-end bg-ink/40" onClick={onClose}>
      <div
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="mx-auto max-h-[85vh] w-full max-w-2xl overflow-y-auto border-2 border-b-0 border-ink bg-sheet"
      >
        <div className="sticky top-0 flex items-center justify-between border-b-2 border-ink bg-paper px-4 py-2">
          <span className="text-[13px] font-black">{title}</span>
          <button type="button" onClick={onClose} aria-label="閉じる" className="px-2 text-lg leading-none font-bold hover:text-shu">
            ×
          </button>
        </div>
        <div className="px-4 py-4">{children}</div>
      </div>
    </div>
  );
}
